import { useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

/**
 * Replaces a module-scope `const styles = StyleSheet.create({...})` — which
 * bakes in whatever `colors.x`/`shadows.x` resolved to at import time and
 * never re-reads them — with a per-render recomputation keyed on the
 * current theme mode. `factory` is the SAME object literal that used to be
 * passed to `StyleSheet.create()` directly at module scope; moving it here
 * (called once per render, memoized so it doesn't rebuild on every
 * unrelated re-render) is what actually makes colors/shadows changes repaint
 * the screen — see theme/colors.ts's own doc comment for why nothing short
 * of re-invoking StyleSheet.create() after a theme change was ever going to
 * work, no matter how `colors` itself was represented.
 */
export function useThemedStyles<T extends StyleSheet.NamedStyles<T> | StyleSheet.NamedStyles<any>>(
  factory: () => T
): T {
  const { themeMode } = useTheme();
  // themeMode is the only thing that should ever invalidate this — factory
  // closes over the (already palette-mutated-in-place) colors/shadows
  // singletons, so re-running it after setColorScheme() has already run is
  // exactly what's needed, and nothing else should trigger a rebuild.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => StyleSheet.create(factory()), [themeMode]);
}
